/**
 * /org — Organigrama y permisos (Org Service, T2 26/10/2026). Pestañas: Mi ficha, Soluciones,
 * Personas (directorio con búsqueda), Clientes y Mi equipo (líderes y roster). La estructura la
 * ve todo clapper; las aristas persona ↔ cliente las recorta el backend por scope.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Users, Building2, Briefcase, CheckCircle2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { StatCard } from '@/components/ui/stat-card';
import { InternalTable, EmptyState, LoaderBlock } from '@/components/internal/shared';
import { usePagination } from '@/hooks/usePagination';
import { useOrgClients, useOrgMe, useOrgPeople, useOrgSolutions, useOrgTeam } from '@/hooks/useOrg';
import { OrgAssignmentsTable, OrgClientLink, OrgPersonCard, OrgPersonLink, OrgSolutionLink, type OrgPerson } from '@/components/org/org-ui';
import { solutionName } from '@/components/org/org-format';
import { useSearch } from '@/hooks/useSearch';
import { OrgManagerPanel } from '@/components/org/OrgManagerPanel';
import { OrgAgentChat } from '@/components/org/OrgAgentChat';

const HOME_TABS = ['me', 'solutions', 'people', 'clients', 'team'] as const;

export function OrgHomePage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') ?? 'me';
  const tab = HOME_TABS.includes(raw as (typeof HOME_TABS)[number]) ? raw : 'me';
  const me = useOrgMe();
  const access = me.data?.access;
  const canTeam = !!access && (access.isLeader || access.canViewAll);
  return (
    <div className="space-y-4 pb-24">
      <div>
        <h1 className="text-2xl font-bold">{t('org.home.title', 'Organigrama y permisos')}</h1>
        <p className="text-sm text-muted-foreground">{t('org.home.subtitle', 'Quién es quién, en qué solución trabaja y con qué clientes. Las asignaciones salen de la app de Carga de Horas y las confirma cada manager al cerrar el mes.')}</p>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="flex h-auto flex-wrap">
          <TabsTrigger value="me">{t('org.tabs.me', 'Mi ficha')}</TabsTrigger>
          <TabsTrigger value="solutions">{t('org.tabs.solutions', 'Soluciones')}</TabsTrigger>
          <TabsTrigger value="people">{t('org.tabs.people', 'Personas')}</TabsTrigger>
          <TabsTrigger value="clients">{t('org.tabs.clients', 'Clientes')}</TabsTrigger>
          {canTeam && <TabsTrigger value="team">{t('org.tabs.team', 'Mi equipo')}</TabsTrigger>}
        </TabsList>
        <TabsContent value="me"><MeTab /></TabsContent>
        <TabsContent value="solutions"><SolutionsTab /></TabsContent>
        <TabsContent value="people"><PeopleTab /></TabsContent>
        <TabsContent value="clients"><ClientsTab /></TabsContent>
        {/* Siempre montada: con /org?tab=team (link del menú) no queda en blanco mientras resuelve el acceso. */}
        <TabsContent value="team"><TeamTab canTeam={canTeam} loading={me.isLoading} canViewAll={!!access?.canViewAll} /></TabsContent>
      </Tabs>
    </div>
  );
}

function MeTab() {
  const { t } = useTranslation();
  const me = useOrgMe();
  if (me.isLoading) return <LoaderBlock />;
  if (me.isError || !me.data) return <EmptyState title={t('org.home.error', 'No se pudo cargar el Org Service. Reintentá en unos segundos.')} />;
  const { person, solution, leader, assignments } = me.data;
  if (!person) {
    return (
      <Alert>
        <AlertTitle>{t('org.home.no_person_title', 'Todavía no estás en el Org Service')}</AlertTitle>
        <AlertDescription>{t('org.home.no_person_body', 'Las personas se sincronizan a diario desde el Community Map. Si entraste hace poco, mañana vas a aparecer; si no, avisá a People.')}</AlertDescription>
      </Alert>
    );
  }
  return (
    <div className="space-y-4">
      <OrgPersonCard person={person} solutionSlug={solution?.slug ?? person.solution_slug} leader={leader} />
      <div>
        <h3 className="mb-2 text-sm font-semibold">{t('org.home.my_assignments', 'Mis asignaciones vigentes')}</h3>
        <OrgAssignmentsTable rows={assignments} hide={['person']} />
      </div>
    </div>
  );
}

function SolutionsTab() {
  const { t } = useTranslation();
  const q = useOrgSolutions();
  if (q.isLoading) return <LoaderBlock />;
  if (q.isError || !q.data) return <EmptyState title={t('org.home.error', 'No se pudo cargar el Org Service. Reintentá en unos segundos.')} />;
  const withClients = q.data.filter((s) => s.worksWithClients);
  const areas = q.data.filter((s) => !s.worksWithClients);
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {withClients.map((s) => (
          <Link key={s.slug} to={`/org/soluciones/${s.slug}`} className="block rounded-xl transition hover:shadow-md">
            <StatCard
              label={s.name}
              value={s.counts.people}
              sub={t('org.home.solution_sub', '{{clients}} clientes · {{confirmed}}/{{assignments}} confirmadas', { clients: s.counts.clients, confirmed: s.counts.confirmed, assignments: s.counts.assignments })}
              icon={Briefcase}
              tone={s.source === 'allocator' ? 'blue' : 'amber'}
            />
          </Link>
        ))}
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold">{t('org.home.areas_title', 'Áreas sin clientes')}</h3>
        <p className="mb-2 text-xs text-muted-foreground">{t('org.home.areas_help', 'Tech, People, Finance y Executive no trabajan por cliente: su acceso es por área (patrón 2).')}</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {areas.map((s) => <StatCard key={s.slug} label={s.name} value={s.counts.people} icon={Users} tone="default" size="sm" />)}
        </div>
      </div>
    </div>
  );
}

function PeopleTab() {
  const { t } = useTranslation();
  const people = useOrgPeople();
  const [q, setQ] = useState('');
  const nameByEmail = useMemo(() => new Map((people.data ?? []).map((p) => [p.email, p.full_name])), [people.data]);
  const filtered = useSearch(people.data, q, (p) => `${p.full_name} ${p.email} ${p.role_title ?? ''} ${p.area ?? ''} ${p.team ?? ''} ${solutionName(p.solution_slug)}`);
  const { pageItems, footer } = usePagination(filtered, 25, q);
  if (people.isLoading) return <LoaderBlock />;
  if (people.isError) return <EmptyState title={t('org.home.error', 'No se pudo cargar el Org Service. Reintentá en unos segundos.')} />;
  const columns = [
    { key: 'name', label: t('org.table.name', 'Nombre'), render: (p: OrgPerson) => <OrgPersonLink email={p.email} name={p.full_name} /> },
    { key: 'role', label: t('org.table.role', 'Puesto'), render: (p: OrgPerson) => <span className="text-muted-foreground">{p.role_title ?? '—'}</span> },
    { key: 'area', label: t('org.card.area', 'Área'), render: (p: OrgPerson) => <span className="text-muted-foreground">{[p.area, p.team].filter(Boolean).join(' · ') || '—'}</span> },
    { key: 'solution', label: t('org.table.solution', 'Solución'), render: (p: OrgPerson) => <OrgSolutionLink slug={p.solution_slug} /> },
    { key: 'leader', label: t('org.card.leader', 'Líder'), render: (p: OrgPerson) => (p.people_manager_email ? <OrgPersonLink email={p.people_manager_email} name={nameByEmail.get(p.people_manager_email) ?? p.people_manager_email} /> : <span className="text-muted-foreground">—</span>) },
  ];
  return (
    <div className="space-y-3">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('org.home.search_people', 'Buscar por nombre, puesto, área o solución…')} className="max-w-md" />
      <p className="text-xs text-muted-foreground">{t('org.home.people_count', '{{count}} personas activas', { count: filtered.length })}</p>
      {filtered.length ? <InternalTable columns={columns} rows={pageItems} rowKey={(p) => String(p.employee_id)} /> : <EmptyState title={t('org.home.no_results', 'Sin resultados')} />}
      {footer}
    </div>
  );
}

type OrgClientRow = NonNullable<ReturnType<typeof useOrgClients>['data']>[number];

function ClientsTab() {
  const { t } = useTranslation();
  const clients = useOrgClients();
  const [q, setQ] = useState('');
  const filtered = useSearch(clients.data, q, (c) => `${c.display_name} ${c.pod ?? ''} ${c.solutions.map(solutionName).join(' ')}`);
  const { pageItems, footer } = usePagination(filtered, 25, q);
  if (clients.isLoading) return <LoaderBlock />;
  if (clients.isError) return <EmptyState title={t('org.home.error', 'No se pudo cargar el Org Service. Reintentá en unos segundos.')} />;
  const columns = [
    { key: 'client', label: t('org.table.client', 'Cliente'), render: (c: OrgClientRow) => <OrgClientLink clientId={c.client_id} name={c.display_name} /> },
    { key: 'pod', label: t('org.table.pod', 'Pod'), render: (c: OrgClientRow) => <span className="text-muted-foreground">{c.pod ?? '—'}</span> },
    { key: 'solutions', label: t('org.table.solutions', 'Soluciones'), render: (c: OrgClientRow) => <span className="text-muted-foreground">{c.solutions.map(solutionName).join(', ') || '—'}</span> },
    { key: 'people', label: t('org.table.people', 'Personas'), align: 'right' as const, render: (c: OrgClientRow) => c.people },
    { key: 'confirmed', label: t('org.table.confirmed', 'Confirmadas'), align: 'right' as const, render: (c: OrgClientRow) => c.confirmed },
  ];
  return (
    <div className="space-y-3">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('org.home.search_clients', 'Buscar cliente, pod o solución…')} className="max-w-md" />
      <p className="text-xs text-muted-foreground">{t('org.home.clients_count', '{{count}} clientes con gente asignada', { count: filtered.length })}</p>
      {filtered.length ? <InternalTable columns={columns} rows={pageItems} rowKey={(c) => c.client_id} /> : <EmptyState title={t('org.home.no_results', 'Sin resultados')} />}
      {footer}
    </div>
  );
}

type TeamPerson = NonNullable<ReturnType<typeof useOrgTeam>['data']>['people'][number];

function TeamTab({ canTeam, loading, canViewAll }: { canTeam: boolean; loading: boolean; canViewAll: boolean }) {
  const { t } = useTranslation();
  const team = useOrgTeam(canTeam);
  if (loading || (canTeam && team.isLoading)) return <LoaderBlock />;
  if (!canTeam) return <EmptyState title={t('org.home.team_no_access', 'Esta pestaña es para quienes tienen gente a cargo en el Community Map o están en los permisos del Org Service.')} />;
  if (team.isError || !team.data) return <EmptyState title={t('org.home.error', 'No se pudo cargar el Org Service. Reintentá en unos segundos.')} />;
  const { people, assignments, missingFromOrg } = team.data;
  const byPerson = new Map<number, number>();
  for (const a of assignments) byPerson.set(a.employee_id, (byPerson.get(a.employee_id) ?? 0) + 1);
  const columns = [
    { key: 'name', label: t('org.table.name', 'Nombre'), render: (p: TeamPerson) => <OrgPersonLink email={p.email} name={p.full_name} /> },
    { key: 'role', label: t('org.table.role', 'Puesto'), render: (p: TeamPerson) => <span className="text-muted-foreground">{p.role_title ?? '—'}</span> },
    { key: 'direct', label: t('org.table.direct', 'Reporte'), render: (p: TeamPerson) => <span className="text-muted-foreground">{p.isDirect ? t('org.table.direct_yes', 'Directo') : t('org.table.direct_via', 'Vía {{leader}}', { leader: p.directLeaderName })}</span> },
    { key: 'solution', label: t('org.table.solution', 'Solución'), render: (p: TeamPerson) => <OrgSolutionLink slug={p.solution_slug} /> },
    { key: 'clients', label: t('org.table.assigned_clients', 'Clientes'), align: 'right' as const, render: (p: TeamPerson) => byPerson.get(p.employee_id) ?? 0 },
  ];
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label={t('org.home.team_people', 'Personas en mi equipo')} value={people.length} icon={Users} tone="blue" size="sm" />
        <StatCard label={t('org.home.team_assignments', 'Asignaciones vigentes')} value={assignments.length} icon={Building2} tone="purple" size="sm" />
        <StatCard label={t('org.home.team_confirmed', 'Confirmadas')} value={assignments.filter((a) => a.status === 'confirmed').length} icon={CheckCircle2} tone="green" size="sm" />
      </div>
      {people.length ? <InternalTable columns={columns} rows={people} rowKey={(p) => String(p.employee_id)} /> : <EmptyState title={t('org.home.team_empty', 'Nadie reporta a esta persona en el Community Map.')} />}
      {missingFromOrg.length > 0 && <p className="text-xs text-muted-foreground">{t('org.home.team_missing', '{{count}} personas del equipo todavía no están en el Org Service (se sincroniza a diario).', { count: missingFromOrg.length })}</p>}
      <OrgManagerPanel people={people} assignments={assignments} canViewAll={canViewAll} />
      <OrgAgentChat />
      <div>
        <h3 className="mb-2 text-sm font-semibold">{t('org.home.team_assignments_title', 'Quién trabaja con qué cliente')}</h3>
        <OrgAssignmentsTable rows={assignments} />
      </div>
    </div>
  );
}
